import { NavLink, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useState, useEffect, useLayoutEffect, useRef } from 'react';
import API from '@/lib/api';
import {
  SquaresFour, Receipt, FileText, ClipboardText, Truck, FileDashed, SlidersHorizontal, Check, ArrowCounterClockwise,
  Article, CashRegister, Bank as BankIcon,
  BellRinging, Package, ArrowsClockwise, ShoppingCart,
  Users, Cube, Car, EnvelopeSimple, Gear, Calculator, SignOut,
  CreditCard, ChartBar, UsersThree, ClockCounterClockwise, Buildings, List, X, Broadcast, GlobeSimple, Tray, Archive, Storefront, MagnifyingGlass, Bell, CurrencyEur, ChartLineUp, TrendUp, Wallet, Barcode, Percent, Crown, AddressBook, CalendarBlank, WhatsappLogo, MapPin, Warning, MapTrifold
} from '@phosphor-icons/react';
import { LANGS } from '@/lib/i18n';
import OnboardingWizard from '@/components/OnboardingWizard';
import OnboardingFeaturesWizard from '@/components/OnboardingFeaturesWizard';
import QuickSearch from '@/components/QuickSearch';
import CommandPalette from '@/components/CommandPalette';
import ScrollToTopFAB from '@/components/ScrollToTopFAB';
import HelpWidget from '@/components/HelpWidget';
import InstallAppButton from '@/components/InstallAppButton';
import OfflineStatus from '@/components/OfflineStatus';
import { useAllFeatures } from '@/hooks/useFeature';
import { useCompanyFunctions } from '@/contexts/CompanyFunctionsContext';
import { useMenuOrder } from '@/hooks/useMenuOrder';
import { MenuOrderEditor } from '@/components/MenuOrderEditor';
import { SidebarShortcuts } from '@/components/SidebarShortcuts';
import { COMPACT_OVERVIEW_ROUTES } from '@/lib/overviewAppearance';
import '@/components/overview-appearance.css';
import { PageBackButton } from '@/components/PageBackButton';

const NAV_SECTIONS_META = {
  'Overzicht':    { color: '#0F172A', bgLight: '#F8FAFC', icon: SquaresFour },
  'Verkoop':      { color: '#002FA7', bgLight: '#EEF3FF', icon: ShoppingCart },
  'Communicatie': { color: '#8B5CF6', bgLight: '#F3E8FF', icon: EnvelopeSimple },
  'Financieel':   { color: '#D97706', bgLight: '#FEF3C7', icon: Wallet },
  'Operationeel': { color: '#7C3AED', bgLight: '#F5F3FF', icon: Package },
  'Beheer':       { color: '#475569', bgLight: '#F1F5F9', icon: Gear },
  'Tools':        { color: '#059669', bgLight: '#ECFDF5', icon: ChartLineUp },
  'Admin':        { color: '#DC2626', bgLight: '#FEE2E2', icon: Crown },
};

export const NAV = [
  { section: 'Overzicht', items: [
    // Dashboard alleen voor admin/alleen-lezen (verkoper ziet enkel zijn eigen rapport, geen globale cijfers).
    { to: '/dashboard', label: 'Dashboard', icon: SquaresFour, adminOnly: true },
    { to: '/zoek-referentie', label: 'Zoek op referentie', icon: MagnifyingGlass, alwaysVisible: true },
  ]},
  { section: 'Verkoop', items: [
    // ── Relaties (blauw) ──
    { to: '/klanten', label: 'Klanten', icon: Users, permKey: 'klanten_aanmaken', kleur: '#3B82F6' },
    { to: '/inkomende-documenten', label: 'Inkomende facturen', icon: Tray, adminOnly: true, kleur: '#3B82F6' },
    { to: '/leveranciers', label: 'Leveranciers', icon: Storefront, adminOnly: true, kleur: '#3B82F6' },
    { to: '/vervoerders', label: 'Vervoerders', icon: Truck, adminOnly: true, kleur: '#3B82F6' },
    // ── Facturatie (groen) ──
    { to: '/facturen', label: 'Facturen', icon: Receipt, permKey: 'facturen_toegang', kleur: '#059669' },
    { to: '/proforma', label: 'Proforma', icon: FileDashed, featureKey: 'doc_proforma', permKey: 'facturen_toegang', kleur: '#059669' },
    // ── Levering (oranje) ──
    { to: '/leveringsbonnen', label: 'Leveringsbonnen', icon: Truck, featureKey: 'doc_leverbon', permKey: 'leverbonnen_toegang', kleur: '#F59E0B' },
    { to: '/leveringsbonnen/snel-scan', label: 'Snel Scanning', icon: Barcode, featureKey: 'doc_leverbon', permKey: 'leverbonnen_toegang', kleur: '#F59E0B' },
    { to: '/routeplanning', label: 'Routeplanning', icon: MapTrifold, featureKey: 'doc_leverbon', permKey: 'leverbonnen_toegang', kleur: '#F59E0B' },
    // ── Bestelbon & Offertes (paars) ──
    { to: '/bestelbonnen', label: 'Bestelbonnen', icon: ClipboardText, featureKey: 'doc_bestelbon', permKey: 'bestelbonnen', kleur: '#7C3AED' },
    { to: '/shop-bestellingen', label: 'Webshop-bestellingen', icon: ShoppingCart, badgeKey: 'shop', permKey: 'bestelbonnen', kleur: '#7C3AED', instellingFlag: 'webshop_actief' },
    { to: '/offertes', label: 'Offertes', icon: FileText, featureKey: 'doc_offerte', permKey: 'offertes_toegang', kleur: '#7C3AED' },
    // ── Creditnota (rood) ──
    { to: '/creditnotas', label: 'Creditnota\'s', icon: CreditCard, featureKey: 'doc_creditnota', permKey: 'creditnotas_toegang', kleur: '#DC2626' },
    // ── Producten & POS (teal) ──
    { to: '/artikels', label: 'Artikels', icon: Article, permKey: 'artikelen_bekijken', kleur: '#0EA5E9' },
    { to: '/kassa', label: 'Kassaverkoop', icon: CashRegister, permKey: 'kassa_gebruiken', kleur: '#0EA5E9' },
    { to: '/verkoop', label: 'Verkoop onderweg', icon: ShoppingCart, alwaysVisible: true, kleur: '#0EA5E9' },
    { to: '/klantbezoeken', label: 'Klantbezoeken', icon: MapPin, alwaysVisible: true, kleur: '#0EA5E9' },
    { to: '/commissies', label: 'Commissies', icon: CurrencyEur, permKey: 'commissies_beheren', permKeyAlt: 'commissies_eigen_zien', kleur: '#0EA5E9' },
  ]},
  { section: 'Communicatie', items: [
    // ── Mail, Agenda, Contacten (paars — productiviteit) ──
    { to: '/mail', label: 'Mail Hub', icon: EnvelopeSimple, adminOnly: true, kleur: '#8B5CF6', badgeKey: 'mail' },
    { to: '/whatsapp', label: 'WhatsApp', icon: WhatsappLogo, adminOnly: true, kleur: '#25D366' },
    { to: '/agenda', label: 'Agenda', icon: CalendarBlank, adminOnly: true, kleur: '#8B5CF6' },
    { to: '/contacten', label: 'Adresboek', icon: AddressBook, adminOnly: true, kleur: '#8B5CF6' },
  ]},
  { section: 'Financieel', items: [
    // ── Cashflow (oranje) ──
    { to: '/bank', label: 'Bankverrichtingen', icon: BankIcon, badgeKey: 'bank', adminOnly: true, kleur: '#F59E0B' },
    // ── Opvolging (rood — vereist actie) ──
    { to: '/herinneringen', label: 'Herinneringen', icon: BellRinging, badgeKey: 'herin', adminOnly: true, kleur: '#DC2626' },
    { to: '/betalingsherinneringen-overzicht', label: 'Betalingsherinneringen', icon: Warning, adminOnly: true, kleur: '#B91C1C' },
  ]},
  { section: 'Operationeel', items: [
    // ── Aankoop (paars) ──
    { to: '/aankoopbestellingen', label: 'Aankoopbestellingen', icon: ClipboardText, adminOnly: true, kleur: '#7C3AED' },
    // ── Voorraad (teal) ──
    { to: '/voorraad', label: 'Voorraad', icon: Package, badgeKey: 'stock', permKey: 'artikelen_bekijken', kleur: '#0EA5E9' },
    // ── Automatisatie (groen) ──
    { to: '/herhaald', label: 'Herhaald', icon: ArrowsClockwise, permKey: 'herhaald_toegang', kleur: '#059669' },
  ]},
  { section: 'Beheer', items: [
    // ── Fiscaal (rood — overheid/verplicht) ──
    { to: '/btw', label: 'BTW Register', icon: Car, adminOnly: true, kleur: '#DC2626' },
    { to: '/btw-aangifte', label: 'BTW Aangifte', icon: Calculator, adminOnly: true, kleur: '#DC2626' },
    // ── Export & communicatie (blauw) ──
    { to: '/boekhoudexport', label: 'Boekhoudexport', icon: Archive, permKey: 'exporteren', kleur: '#3B82F6' },
    // ── Organisatie (paars) ──
    { to: '/bedrijven', label: 'Bedrijven', icon: Buildings, adminOnly: true, kleur: '#7C3AED' },
  ]},
  { section: 'Tools', items: [
    // ── Verkoop-rapporten (groen) ──
    { to: '/verkoop-rapport', label: 'Verkooprapport', icon: ChartLineUp, permKey: 'verkooprapport_bekijken', kleur: '#059669' },
    { to: '/kosten-rapport', label: 'Kostenrapport', icon: Wallet, adminOnly: true, kleur: '#059669' },
    // ── Top-lijsten (oranje) ──
    { to: '/top-artikels', label: 'Top verkocht', icon: TrendUp, permKey: 'rapporten_bekijken', kleur: '#F59E0B' },
    { to: '/top-klanten', label: 'Top klanten', icon: UsersThree, permKey: 'rapporten_bekijken', kleur: '#F59E0B' },
    // ── Audits & analyse (paars) ──
    { to: '/kortingen-audit', label: 'Kortingen audit', icon: Percent, permKey: 'rapporten_bekijken', kleur: '#7C3AED' },
    { to: '/rapportage', label: 'Rapportage', icon: ChartBar, permKey: 'rapporten_bekijken', kleur: '#7C3AED' },
    // ── Technisch (grijsblauw) ──
    { to: '/webhook-monitor', label: 'Webhooks', icon: Broadcast, adminOnly: true, kleur: '#475569' },
  ]},
  { section: 'Admin', items: [
    // ── Logs (grijsblauw) ──
    { to: '/audit-log', label: 'Activiteitenlog', icon: ClockCounterClockwise, adminOnly: true, kleur: '#475569' },
    // ── Configuratie (rood) ──
    { to: '/instellingen', label: 'Instellingen', icon: Gear, permKey: 'instellingen_wijzigen', kleur: '#DC2626', kleurLabel: true },
    // ── Abonnement (groen — premium upgrade) ──
    { to: '/abonnement', label: 'Abonnement', icon: CreditCard, adminOnly: true, kleur: '#059669', kleurLabel: true },
    // ── Super-Admin Console (alleen voor BillFac platform-eigenaar) ──
    { to: '/super-admin', label: 'Super-Admin', icon: Crown, superAdminOnly: true, kleur: '#7C3AED', kleurLabel: true },
  ]},
];

export default function Layout() {
  const { user, logout } = useAuth();
  const { data: functionConfig, error: functionError, reload: reloadFunctions, isVisible } = useCompanyFunctions();
  const navigate = useNavigate();
  const location = useLocation();
  const mainRef = useRef(null);
  useLayoutEffect(() => {
    mainRef.current?.scrollTo({ top: 0, behavior: 'instant' });
  }, [location.pathname]);
  const [badges, setBadges] = useState({ bank: 0, herin: 0, stock: 0, notif: 0, shop: 0, mail: 0 });
  const [instFlags, setInstFlags] = useState({ webshop_actief: false });
  const [mobileOpen, setMobileOpen] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [showFeaturesWizard, setShowFeaturesWizard] = useState(false);
  const [quickOpen, setQuickOpen] = useState(false);
  const [editingMenu, setEditingMenu] = useState(false);
  const [shortcutSettingsOpen, setShortcutSettingsOpen] = useState(false);
  const menuOrder = useMenuOrder(user, NAV);

  const isAdmin = user?.role === 'admin';
  const isVerkoper = user?.role === 'verkoper';
  const features = useAllFeatures();
  const isReadOnly = user?.role === 'alleen-lezen';
  // Helper: admin heeft altijd toegang, anders check user.machtigingen
  const hasPerm = (key) => !!(isAdmin || (user?.machtigingen || {})[key]);
  const canCommissies = hasPerm('commissies_beheren');
  const [bedrijven, setBedrijven] = useState([]);
  const [actiefBedrijf, setActiefBedrijf] = useState('');
  // Sidebar modus: 'accordion' (één sectie open) of 'expanded' (alles open). Persisteerd in localStorage.
  const [sidebarMode, setSidebarMode] = useState(() => {
    try { return localStorage.getItem('sidebar_mode') || 'accordion'; }
    catch { return 'accordion'; }
  });
  const toggleSidebarMode = () => {
    setSidebarMode(prev => {
      const next = prev === 'accordion' ? 'expanded' : 'accordion';
      try { localStorage.setItem('sidebar_mode', next); } catch {}
      return next;
    });
  };
  // Accordion-gedrag: ÉÉN sectie open tegelijk. localStorage onthoudt welke.
  // Standaard: alle dicht behalve eerst opgeslagen sectie (of geen).
  const [openSection, setOpenSection] = useState(() => {
    try { return localStorage.getItem('sidebar_open_section') || ''; }
    catch { return ''; }
  });
  const toggleSection = (sec) => {
    setOpenSection(prev => {
      const next = prev === sec ? '' : sec;  // klik op open sectie → sluit; anders open + sluit rest
      try { localStorage.setItem('sidebar_open_section', next); } catch {}
      return next;
    });
  };

  useEffect(() => {
    API.get('/dashboard').then(r => {
      setBadges(prev => ({
        ...prev,
        bank: r.data.bank_ongekoppeld || 0,
        herin: (r.data.recente_docs || []).filter(d => d.type === 'factuur' && (d.status === 'openstaand' || d.status === 'vervallen')).length,
        stock: (r.data.kritieke_stock || []).length,
      }));
    }).catch(() => {});
    API.get('/notificaties/count').then(r => {
      setBadges(prev => ({ ...prev, notif: r.data.unread || 0 }));
    }).catch(() => {});
    API.get('/shop-bestellingen/badge').then(r => {
      setBadges(prev => ({ ...prev, shop: r.data.n_nieuw || 0 }));
      setInstFlags(prev => ({ ...prev, webshop_actief: !!r.data.webshop_actief }));
    }).catch(() => {});
    API.get('/onboarding/status').then(r => {
      if (!r.data.onboarding_done && !r.data.completed) setShowOnboarding(true);
      else {
        // Basis onboarding klaar → toon features-wizard indien feature actief en nog niet gedaan
        API.get('/instellingen').then(i => {
          const inst = i.data || {};
          const feat = (inst.features || {}).onboarding_wizard;
          const actief = feat ? feat.actief : true;
          if (actief && !inst.features_onboarding_done) setShowFeaturesWizard(true);
        }).catch(() => {});
      }
    }).catch(() => {});
    if (isAdmin) {
      API.get('/bedrijven').then(r => setBedrijven(r.data)).catch(() => {});
      API.get('/auth/actief-bedrijf').then(r => setActiefBedrijf(r.data.actief_bedrijf || '')).catch(() => {});
    }
    // Mail-counts (ongelezen badge) — best-effort, faalt stil bij 402 (non-premium)
    const loadMailBadge = () => {
      API.get('/mail/counts').then(r => {
        setBadges(prev => ({ ...prev, mail: r.data?.unread_total || 0 }));
      }).catch(() => { /* premium niet actief of geen mailbox */ });
    };
    loadMailBadge();
    // Refresh elke 60s
    const mailInterval = setInterval(loadMailBadge, 60000);
    return () => clearInterval(mailInterval);
  }, [isAdmin]);

  // Close mobile menu on nav
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  // Accordion: open automatisch de sectie waarin de actieve route zit (alleen als de gebruiker
  // niet recent zelf een andere sectie heeft geopend — dit voorkomt dat de UI tegen de gebruiker werkt).
  // In expanded-modus is dit niet nodig (alles staat al open).
  useEffect(() => {
    if (sidebarMode === 'expanded' || editingMenu) return;
    const path = location.pathname;
    const match = menuOrder.groups.flatMap(g => g.items.map(item => ({ ...item, section: g.section })))
      .filter(item => path === item.to || path.startsWith(item.to + '/')).sort((a, b) => b.to.length - a.to.length)[0];
    if (match) {
      setOpenSection(match.section);
      try { localStorage.setItem('sidebar_open_section', match.section); } catch {}
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, sidebarMode, editingMenu, menuOrder.groups]);

  // Global keyboard shortcut: Ctrl+K / Cmd+K opent CommandPalette (zie CommandPalette component)
  // (Oude QuickSearch is vervangen — laat binding weg zodat CommandPalette zelf handelt)
  useEffect(() => { /* no-op */ }, []);

  const [backendAlive, setBackendAlive] = useState(true);
  useEffect(() => {
    let t;
    const ping = () => {
      API.get('/health', { timeout: 4000, noCache: true })
        .then(() => setBackendAlive(true))
        .catch(() => setBackendAlive(false));
    };
    ping();
    t = setInterval(ping, 60000);
    return () => clearInterval(t);
  }, []);

  // Dynamische branding: leest app_naam uit /api/instellingen — fallback "BillFac"
  // Hierdoor kan de admin de merknaam veranderen in Instellingen → Branding zonder code-wijziging.
  const [branding, setBranding] = useState({ app_naam: 'BillFac', primary_kleur: '#002FA7', firma_naam: '', firma_logo: '' });
  // SaaS multi-tenant: trial-info voor banner ("nog X dagen proefperiode")
  const [tenant, setTenant] = useState(null);
  useEffect(() => {
    API.get('/tenant/me').then(r => setTenant(r.data)).catch(() => {});
  }, []);
  useEffect(() => {
    API.get('/instellingen').then(r => {
      const inst = r.data || {};
      const logoRaw = inst.logo_url || inst.logo || '';
      // Maak absolute URL — /api/uploads/... krijgt backend-prefix
      let logoUrl = '';
      if (logoRaw) {
        if (logoRaw.startsWith('http') || logoRaw.startsWith('data:')) logoUrl = logoRaw;
        else if (logoRaw.startsWith('/api/')) logoUrl = `${process.env.REACT_APP_BACKEND_URL}${logoRaw}`;
        else logoUrl = logoRaw;
      }
      setBranding({
        app_naam: inst.app_naam || 'BillFac',
        primary_kleur: inst.primary_kleur || '#002FA7',
        accent_kleur: inst.accent_kleur || '#22C55E',
        firma_naam: inst.naam || '',  // bedrijfsnaam ingelogde tenant
        firma_logo: logoUrl,
      });
      // Apply colors als CSS custom properties op de :root zodat ALLE pagina's ze gebruiken
      const primary = inst.primary_kleur || '#002FA7';
      const accent = inst.accent_kleur || '#22C55E';
      const root = document.documentElement;
      root.style.setProperty('--brand-primary', primary);
      root.style.setProperty('--brand-accent', accent);
      // Hover-variant (10% donkerder)
      const darken = (hex, amt = 0.12) => {
        const m = hex.match(/^#([0-9a-f]{6})$/i);
        if (!m) return hex;
        const r = parseInt(m[1].slice(0,2), 16);
        const g = parseInt(m[1].slice(2,4), 16);
        const b = parseInt(m[1].slice(4,6), 16);
        const f = (c) => Math.max(0, Math.min(255, Math.round(c * (1 - amt))));
        return `#${[f(r),f(g),f(b)].map(c => c.toString(16).padStart(2,'0')).join('')}`;
      };
      root.style.setProperty('--brand-primary-hover', darken(primary, 0.12));
      root.style.setProperty('--brand-accent-hover', darken(accent, 0.12));
    }).catch(() => {});
  }, []);

  // Splits app_naam in eerste woord (gekleurd) + rest (zwart). Bv "BillFac" → ["Bill","Fac"]
  // Voor camelCase namen (BillFac, EmergentBilling) → split na de eerste hoofdletter ná positie 0.
  // Voor namen met spaties (Mijn Bedrijf) → split op de spatie.
  const splitBrandName = (naam) => {
    if (!naam) return ['Bill', 'Fac'];
    const sp = naam.indexOf(' ');
    if (sp > 0) return [naam.slice(0, sp), naam.slice(sp + 1)];
    const m = naam.slice(1).search(/[A-Z]/);
    if (m >= 0) return [naam.slice(0, m + 1), naam.slice(m + 1)];
    return [naam, ''];
  };
  const [brandEerste, brandRest] = splitBrandName(branding.app_naam);

  // Dynamische browser-tab titel: toont ongelezen notificaties + openstaande acties
  useEffect(() => {
    const total = (badges.notif || 0) + (badges.bank || 0);
    document.title = total > 0 ? `(${total}) ${branding.app_naam}` : branding.app_naam;
  }, [badges, branding.app_naam]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const roleBadge = isAdmin ? { label: 'Admin', color: '#DC2626' } :
    isReadOnly ? { label: 'Alleen lezen', color: '#6B7280' } :
    isVerkoper ? { label: 'Verkoper', color: '#0EA5E9' } :
    { label: 'Gebruiker', color: '#002FA7' };

  const visibleMenuItem = item => {
    if (!isVisible(item.to)) return false;
    if (item.adminOnly && !isAdmin) return false;
    if (item.superAdminOnly && !user?.is_super_admin) return false;
    if (item.hideForVerkoper && isVerkoper) return false;
    if (item.permKey && !hasPerm(item.permKey) && !(item.permKeyAlt && hasPerm(item.permKeyAlt))) return false;
    if (item.featureKey && features && features[item.featureKey] === false) return false;
    if (item.instellingFlag && !instFlags[item.instellingFlag]) return false;
    if (!isAdmin && !item.alwaysVisible && !item.permKey && !item.adminOnly) return false;
    return true;
  };

  const sidebarContent = (
    <>
      <div className="px-5 py-4 border-b border-gray-200 flex items-start justify-between">
        <div className="flex flex-col gap-0.5 min-w-0">
          {branding.app_naam === 'BillFac' ? (
            // Officiële BillFac merk: gebruik SVG logo
            <span className="flex items-center gap-2" data-testid="brand-logo">
              <img src="/logo.svg" alt="BillFac" className="h-7 w-7 flex-shrink-0" />
              <span className="text-lg font-extrabold tracking-tight" style={{ fontFamily: 'var(--font-heading)' }}>
                <span style={{ color: branding.primary_kleur }}>Bill</span>
                <span className="text-gray-900">Fac</span>
              </span>
              <span
                className={`inline-block w-1.5 h-1.5 rounded-full ${backendAlive ? 'bg-green-500' : 'bg-red-500'}`}
                title={backendAlive ? 'Online — backend bereikbaar' : 'Offline — geen verbinding met server'}
                data-testid="backend-status"
              />
            </span>
          ) : (
            // White-label / tenant met eigen app_naam
            <span className="text-lg font-extrabold tracking-tight flex items-center gap-1.5" style={{ fontFamily: 'var(--font-heading)' }} data-testid="brand-logo">
              <span className="flex items-baseline">
                <span style={{ color: branding.primary_kleur }}>{brandEerste}</span>
                {brandRest && <span>{brandRest}</span>}
              </span>
              <span
                className={`inline-block w-1.5 h-1.5 rounded-full ${backendAlive ? 'bg-green-500' : 'bg-red-500'}`}
                title={backendAlive ? 'Online — backend bereikbaar' : 'Offline — geen verbinding met server'}
                data-testid="backend-status"
              />
            </span>
          )}
          {(branding.firma_naam || branding.firma_logo) && (
            <div className="flex items-center gap-1.5 mt-0.5" data-testid="brand-firma">
              {branding.firma_logo && (
                <img
                  src={branding.firma_logo}
                  alt={branding.firma_naam || 'logo'}
                  className="h-5 w-5 object-contain rounded border border-gray-200 bg-white flex-shrink-0"
                  onError={(e) => { e.target.style.display = 'none'; }}
                  data-testid="brand-firma-logo"
                />
              )}
              {branding.firma_naam && (
                <span className="text-[11px] font-semibold text-gray-500 truncate uppercase tracking-wide" title={branding.firma_naam} data-testid="brand-firma-naam">
                  {branding.firma_naam}
                </span>
              )}
            </div>
          )}
        </div>
        <button className="md:hidden btn btn-ghost btn-sm" onClick={() => setMobileOpen(false)} data-testid="mobile-menu-close" aria-label="Menu sluiten">
          <X size={18} />
        </button>
      </div>

      <SidebarShortcuts user={user} navigation={NAV} visible={visibleMenuItem} readOnly={isReadOnly}
        open={shortcutSettingsOpen} onOpenChange={setShortcutSettingsOpen}
        onNavigate={to => { setMobileOpen(false); navigate(to); }}
        onSearch={() => { setMobileOpen(false); window.dispatchEvent(new CustomEvent('open-cmdk')); }} />

      {/* Bedrijf switcher */}
      {isAdmin && bedrijven.length > 0 && (
        <div className="px-4 py-2 border-b border-gray-200 bg-[#F8FAFC]">
          <select className="w-full text-xs font-medium bg-white border border-gray-200 px-2 py-1.5 cursor-pointer"
            value={actiefBedrijf}
            onChange={async (e) => {
              const bid = e.target.value;
              setActiefBedrijf(bid);
              await API.put('/auth/switch-bedrijf', { bedrijf_id: bid });
              window.location.reload();
            }}
            data-testid="bedrijf-switcher">
            <option value="">Alle bedrijven</option>
            {bedrijven.map(b => <option key={b.id} value={b.id}>{b.naam}</option>)}
          </select>
        </div>
      )}

      <nav className="flex-1 min-h-0 py-2 overflow-y-auto" data-testid="sidebar-navigation">
        <div className="sticky top-0 z-10 bg-white border-b border-gray-100 px-3 pb-2 mb-2">
          <div className="flex items-center justify-between gap-1">
            <button type="button" onClick={() => setEditingMenu(v => !v)} disabled={!menuOrder.canEdit}
              className="flex items-center gap-1.5 py-1.5 text-xs font-semibold text-gray-600 hover:text-blue-700 transition-colors disabled:opacity-40"
              aria-pressed={editingMenu} data-testid="menu-order-toggle">{editingMenu ? <Check size={15} /> : <SlidersHorizontal size={15} />}{editingMenu ? 'Klaar' : 'Menu indelen'}</button>
            <button type="button" onClick={() => setShortcutSettingsOpen(true)} className="p-2 text-gray-500 hover:text-blue-700 transition-colors" title="Snelknoppen aanpassen" aria-label="Snelknoppen aanpassen" data-testid="shortcut-settings-open"><Gear size={16} /></button>
            {editingMenu && <button type="button" onClick={() => { if (window.confirm('Je persoonlijke menu terugzetten naar de standaardvolgorde?')) menuOrder.reset(); }}
              disabled={!menuOrder.canEdit} title="Standaardvolgorde herstellen" aria-label="Standaardvolgorde herstellen"
              className="p-2 text-gray-400 hover:text-blue-700 transition-colors disabled:opacity-40" data-testid="menu-order-reset"><ArrowCounterClockwise size={16} /></button>}
          </div>
          {(editingMenu || menuOrder.error) && <div role={menuOrder.error ? 'alert' : 'status'} aria-live="polite" data-testid="menu-order-status"
            className={`text-[11px] ${menuOrder.error ? 'text-red-700' : 'text-gray-500'}`}>
            {menuOrder.error || (menuOrder.status === 'saving' ? 'Bewaren…' : menuOrder.status === 'saved' ? 'Bewaard voor jou' : 'Persoonlijk menu')}
            {menuOrder.error && <button type="button" className="block underline py-1" onClick={menuOrder.retry} data-testid="menu-order-retry">Opnieuw proberen</button>}
          </div>}
        </div>
        {/* Sidebar modus-toggle (iOS-stijl switch): accordion vs alles uitgeklapt */}
        {!editingMenu && <div className="px-5 pb-2 flex items-center justify-between gap-2">
          <span className="text-[10px] text-gray-500 font-semibold uppercase tracking-wide">
            {sidebarMode === 'expanded' ? 'Alles uitgeklapt' : 'Accordion'}
          </span>
          <button
            type="button"
            onClick={toggleSidebarMode}
            role="switch"
            aria-checked={sidebarMode === 'expanded'}
            className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-[#002FA7]`}
            style={{ background: sidebarMode === 'expanded' ? '#002FA7' : '#D1D5DB' }}
            title={sidebarMode === 'expanded' ? 'Schakel naar accordion (één sectie open)' : 'Klap alles uit (alle secties open)'}
            data-testid="sidebar-mode-toggle"
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-md transition-transform duration-200 ease-in-out ${
                sidebarMode === 'expanded' ? 'translate-x-[18px]' : 'translate-x-[2px]'
              }`}
            />
          </button>
        </div>}
        {editingMenu ? <MenuOrderEditor groups={menuOrder.groups} visible={visibleMenuItem} disabled={!menuOrder.canEdit} onChange={menuOrder.change} /> : menuOrder.groups.map((g, gi) => {
          const meta = NAV_SECTIONS_META[g.section] || { color: '#6B7280', bgLight: '#F9FAFB' };
          // Accordion-modus: enkel geselecteerde sectie uit. Expanded-modus: alles open.
          const collapsed = sidebarMode === 'expanded' ? false : (openSection !== g.section);
          // Filter items eerst — als de hele sectie leeg is voor deze gebruiker, sla hem volledig over
          const visibleItems = g.items.filter(visibleMenuItem);
          if (visibleItems.length === 0) return null;
          return (
          <div key={gi}>
            {g.section && (
              <button
                type="button"
                onClick={() => toggleSection(g.section)}
                className="w-full px-5 pt-4 pb-1.5 flex items-center gap-2.5 group hover:opacity-80 transition-opacity"
                data-testid={`nav-section-${g.section.toLowerCase()}`}
              >
                {meta.icon && (
                  <meta.icon
                    size={18}
                    weight={collapsed ? 'regular' : 'fill'}
                    style={{ color: meta.color }}
                    className="flex-shrink-0"
                  />
                )}
                <span className="text-[12px] font-bold uppercase tracking-wider flex-1 text-left" style={{ color: meta.color }}>
                  {g.section}
                </span>
                <span
                  className="text-[16px] font-bold leading-none group-hover:opacity-80 transition-opacity"
                  style={{ color: meta.color }}
                  aria-hidden="true"
                >
                  {collapsed ? '▸' : '▾'}
                </span>
              </button>
            )}
            {!collapsed && visibleItems.map(item => {
              const Icon = item.icon;
              const badge = item.badgeKey ? badges[item.badgeKey] : 0;
              const itemKleur = item.kleur || null;
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  onClick={() => setMobileOpen(false)}
                  className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
                  style={({ isActive }) => isActive ? {
                    background: meta.bgLight,
                    borderLeft: `3px solid ${itemKleur || meta.color}`,
                    paddingLeft: 'calc(1.25rem - 3px)',
                    color: itemKleur || meta.color,
                    fontWeight: 700,
                  } : undefined}
                  data-testid={`nav-${item.to.slice(1)}`}
                >
                  <Icon size={16} weight="bold" color={itemKleur || undefined} />
                  <span className="flex-1" style={item.kleurLabel && itemKleur ? { color: itemKleur, fontWeight: 600 } : undefined}>{item.label}</span>
                  {badge > 0 && (
                    <span className="bg-red-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full leading-none">
                      {badge}
                    </span>
                  )}
                </NavLink>
              );
            })}
          </div>
          );
        })}
      </nav>

      <div className="border-t border-gray-200 p-4" style={{ paddingBottom: 'calc(1rem + env(safe-area-inset-bottom, 0px))' }}>
        {/* Notificaties bell */}
        {isVisible('/notificaties') && <NavLink
          to="/notificaties"
          className="flex items-center gap-2 mb-2 p-1.5 hover:bg-gray-50 transition-colors relative"
          data-testid="notif-bell"
        >
          <div className="relative">
            <Bell size={16} className={badges.notif > 0 ? 'text-[#002FA7]' : 'text-gray-400'} weight={badges.notif > 0 ? 'fill' : 'regular'} />
            {badges.notif > 0 && (
              <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[8px] font-bold rounded-full min-w-[14px] h-[14px] px-1 flex items-center justify-center" data-testid="notif-badge">
                {badges.notif > 99 ? '99+' : badges.notif}
              </span>
            )}
          </div>
          <span className="text-xs font-semibold text-gray-700">Notificaties</span>
          {badges.notif > 0 && <span className="text-[10px] text-red-500 font-bold ml-auto">{badges.notif} nieuw</span>}
        </NavLink>}
        {/* Language switcher */}
        <div className="flex items-center gap-1 mb-2" data-testid="lang-switcher">
          <GlobeSimple size={12} className="text-gray-400" />
          {LANGS.map(l => (
            <button key={l.code}
              className={`text-[10px] font-bold px-1.5 py-0.5 transition-colors ${
                (localStorage.getItem('doc_lang') || 'nl') === l.code
                  ? 'bg-[#002FA7] text-white' : 'text-gray-400 hover:text-gray-600'
              }`}
              onClick={() => { localStorage.setItem('doc_lang', l.code); window.dispatchEvent(new Event('langchange')); }}
              title={l.full}
              data-testid={`lang-${l.code}`}>
              {l.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2 mb-1">
          <div className="text-xs text-gray-500 truncate flex-1">{user?.name || user?.email}</div>
          <span className="text-[9px] font-bold px-1.5 py-0.5 shrink-0" style={{ color: roleBadge.color, background: roleBadge.color + '15', border: `1px solid ${roleBadge.color}30` }}>
            {roleBadge.label}
          </span>
        </div>
        <button onClick={handleLogout}
          className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-red-600 transition-colors"
          data-testid="logout-btn">
          <SignOut size={14} /> Uitloggen
        </button>
        <InstallAppButton variant="compact" />
      </div>
    </>
  );

  return (
    <div className="flex h-screen bg-[#FAFAFA]" data-testid="app-layout">
      {/* Mobile top bar met hamburger — vaste balk + iOS safe-area (notch/statusbalk) */}
      <div className="fixed top-0 left-0 right-0 z-40 md:hidden bg-white border-b border-gray-200 flex items-end gap-1 px-1.5 shadow-sm" style={{ paddingTop: 'env(safe-area-inset-top, 0px)', height: 'calc(3rem + env(safe-area-inset-top, 0px))' }} data-testid="mobile-topbar">
        <button
          className="flex items-center justify-center w-11 h-11 shrink-0 text-gray-800 rounded active:bg-gray-100"
          onClick={() => setMobileOpen(prev => !prev)}
          aria-label="Menu openen"
          data-testid="mobile-menu-btn"
        >
          <List size={24} weight="bold" />
        </button>
        <span className="text-base font-extrabold tracking-tight truncate min-w-0 flex-1" style={{ fontFamily: 'var(--font-heading)' }}>
          <span style={{ color: branding.primary_kleur }}>{brandEerste}</span>
          {brandRest && <span className="text-gray-900">{brandRest}</span>}
        </span>
      </div>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 z-40 bg-black/30 md:hidden" onClick={() => setMobileOpen(false)} data-testid="mobile-menu-overlay" />
      )}

      {/* Sidebar - desktop: always visible, mobile: slide-in */}
      <aside className={`
        w-56 border-r border-gray-200 bg-white flex flex-col shrink-0
        fixed md:static inset-y-0 left-0 z-50
        transform transition-transform duration-200
        ${mobileOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
      `} style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }} data-testid="sidebar">
        {sidebarContent}
      </aside>

      <main ref={mainRef} className="flex-1 min-w-0 overflow-y-auto p-4 pt-[calc(3.5rem+env(safe-area-inset-top,0px))] md:p-6 md:pt-6 lg:p-8" data-testid="main-content" data-overview-style={COMPACT_OVERVIEW_ROUTES.has(location.pathname) ? 'compact' : undefined}>
        <nav className="mb-4 flex items-center" aria-label="Paginanavigatie" data-testid="page-back-toolbar" data-page-back-navigation>
          <span className="hidden md:inline-flex"><PageBackButton testId="page-back-desktop" /></span>
          <span className="inline-flex md:hidden"><PageBackButton testId="page-back-mobile" /></span>
        </nav>
        {!functionConfig && functionError && !location.pathname.startsWith('/instellingen/functies') && (
          <div role="alert" className="bg-amber-50 p-3 mb-4 text-sm text-amber-800" data-testid="navigation-functions-error">
            {functionError} <button onClick={reloadFunctions} className="underline" data-testid="navigation-functions-retry">Opnieuw laden</button>
          </div>
        )}
        {/* Trial-banner: toont resterende proefperiode dagen. Gewaarschuwde kleur als <=3 dagen. */}
        {tenant?.is_trial && tenant?.trial_dagen_over !== null && tenant?.trial_dagen_over !== undefined && (
          <div
            className={`mb-4 px-4 py-2.5 border text-sm font-medium flex items-center justify-between gap-3 rounded ${
              tenant.trial_dagen_over <= 3
                ? 'bg-amber-50 border-amber-300 text-amber-800'
                : 'bg-blue-50 border-blue-200 text-blue-800'
            }`}
            data-testid="trial-banner"
          >
            <div className="flex items-center gap-2">
              <span>🎁</span>
              <span>
                <strong>Gratis proefperiode</strong> — nog {tenant.trial_dagen_over} dag{tenant.trial_dagen_over === 1 ? '' : 'en'} over
              </span>
            </div>
            <button
              onClick={() => navigate('/abonnement')}
              className={`px-3 py-1 text-xs font-bold uppercase tracking-wide rounded transition-colors ${
                tenant.trial_dagen_over <= 3 ? 'bg-amber-600 text-white hover:bg-amber-700' : 'bg-[#002FA7] text-white hover:bg-[#001B66]'
              }`}
              data-testid="trial-banner-upgrade"
            >
              Upgrade naar betaald
            </button>
          </div>
        )}
        {isReadOnly && (
          <div className="mb-4 px-3 py-2 bg-gray-100 border border-gray-200 text-gray-500 text-xs font-medium" data-testid="readonly-banner">
            Je hebt alleen-lezen rechten. Wijzigingen zijn niet mogelijk.
          </div>
        )}
        <Outlet context={{ isReadOnly, isAdmin }} />
      </main>

      {showOnboarding && <OnboardingWizard onComplete={() => setShowOnboarding(false)} />}
      {showFeaturesWizard && <OnboardingFeaturesWizard onClose={() => setShowFeaturesWizard(false)} onComplete={() => setShowFeaturesWizard(false)} />}
      <QuickSearch open={false} onClose={() => setQuickOpen(false)} />
      <CommandPalette />
      <ScrollToTopFAB threshold={400} />
      <HelpWidget />
      <OfflineStatus />
    </div>
  );
}
